import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import PlayerAvatar from "../components/PlayerAvatar";
import Stars from "../components/Stars";
import { Plus, Trash, CalendarBlank, BookmarkSimple, FolderOpen } from "@phosphor-icons/react";

const PRACTICE_EVAL_FIELDS = [
  "attendance", "passing", "dribbling", "shooting", "defense", "attitude", "listening", "teamwork",
];

const emptyPractice = { title: "", practice_date: new Date().toISOString().slice(0, 10), duration_minutes: 90, activities: [""], notes: "" };

export default function Practices() {
  const [practices, setPractices] = useState([]);
  const [players, setPlayers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyPractice);
  const [evalPlayer, setEvalPlayer] = useState(null);
  const [ratings, setRatings] = useState({});
  const [savingTpl, setSavingTpl] = useState(false);

  async function reload() {
    const [pr, ps, tp] = await Promise.all([
      api.get("/practices"),
      api.get("/players"),
      api.get("/practice-templates"),
    ]);
    setPractices(pr.data); setPlayers(ps.data); setTemplates(tp.data);
  }
  useEffect(() => { reload(); }, []);

  async function createPractice(e) {
    e.preventDefault();
    try {
      await api.post("/practices", { ...form, activities: form.activities.filter(Boolean) });
      toast.success("Practice created");
      setForm(emptyPractice); setShowForm(false); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function saveAsTemplate() {
    const name = window.prompt("Template name:", form.title || "New Template");
    if (!name?.trim()) return;
    setSavingTpl(true);
    try {
      await api.post("/practice-templates", {
        name: name.trim(),
        duration_minutes: form.duration_minutes,
        activities: form.activities.filter(Boolean),
        notes: form.notes,
      });
      toast.success("Saved as template");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingTpl(false); }
  }

  function loadTemplate(t) {
    setForm({
      ...form,
      title: t.name,
      duration_minutes: t.duration_minutes || 90,
      activities: t.activities?.length ? [...t.activities] : [""],
      notes: t.notes || "",
    });
    setShowForm(true);
    toast.success(`Loaded template: ${t.name}`);
  }

  async function deleteTemplate(id) {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/practice-templates/${id}`); reload();
  }

  async function remove(id) {
    if (!window.confirm("Delete this practice?")) return;
    await api.delete(`/practices/${id}`); reload();
  }

  async function saveEval() {
    if (!evalPlayer) return;
    try {
      await api.post("/practice-evals", { player_id: evalPlayer.id, ...ratings });
      toast.success("Evaluation saved");
      setEvalPlayer(null); setRatings({});
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const inputCls = "w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Sessions</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Practices</h1>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="add-practice-btn"
          className="h-14 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider inline-flex items-center gap-2"
        >
          <Plus size={22} weight="bold" /> New Practice
        </button>
      </div>

      {showForm && (
        <form onSubmit={createPractice} className="bg-[#121212] border border-neutral-800 rounded-lg p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input required placeholder="Title (e.g. Speed drills)" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="practice-title" />
            <input required type="date" className={inputCls} value={form.practice_date} onChange={(e) => setForm({ ...form, practice_date: e.target.value })} />
            <input type="number" min="15" className={inputCls} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: +e.target.value })} placeholder="Duration (min)" />
          </div>
          <div>
            <div className="overline mb-2">Activities</div>
            {form.activities.map((a, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  className={inputCls}
                  placeholder={`Activity ${i + 1}`}
                  value={a}
                  onChange={(e) => setForm({ ...form, activities: form.activities.map((x, j) => j === i ? e.target.value : x) })}
                />
                <button type="button" onClick={() => setForm({ ...form, activities: form.activities.filter((_, j) => j !== i) })} className="w-12 h-12 rounded-md border border-neutral-800 hover:border-[#D90429] hover:text-[#D90429] flex items-center justify-center">
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, activities: [...form.activities, ""] })} className="h-10 px-4 text-xs font-bold uppercase tracking-wider border border-neutral-800 rounded-md hover:border-[#06D6A0] hover:text-[#06D6A0]">+ Activity</button>
          </div>
          <textarea rows={2} placeholder="Notes" className="w-full p-3 bg-[#0a0a0a] border border-neutral-800 rounded-md" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex flex-wrap gap-2">
            <button type="submit" className="h-12 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider" data-testid="submit-practice-btn">Save Practice</button>
            <button
              type="button"
              onClick={saveAsTemplate}
              disabled={savingTpl || !form.title || !form.activities.some(Boolean)}
              data-testid="save-as-template-btn"
              className="h-12 px-6 rounded-md border border-neutral-800 hover:border-[#06D6A0] hover:text-[#06D6A0] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-40"
            >
              <BookmarkSimple size={16} weight="bold" /> Save as Template
            </button>
          </div>
        </form>
      )}

      {templates.length > 0 && (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6">
          <div className="overline mb-3 flex items-center gap-2"><FolderOpen size={14} weight="bold" /> Practice Templates</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map((t) => (
              <div key={t.id} className="bg-[#0a0a0a] border border-neutral-800 rounded-md p-3 flex flex-col">
                <div className="font-display font-bold text-lg uppercase tracking-tight">{t.name}</div>
                <div className="text-xs text-neutral-500 mt-1">{t.duration_minutes} min • {t.activities?.length || 0} activities</div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => loadTemplate(t)}
                    data-testid={`use-template-${t.id}`}
                    className="flex-1 h-10 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white text-xs font-bold uppercase tracking-wider"
                  >
                    Use
                  </button>
                  <button
                    onClick={() => deleteTemplate(t.id)}
                    className="h-10 w-10 rounded-md border border-neutral-800 hover:border-[#D90429] hover:text-[#D90429] flex items-center justify-center"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {practices.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {practices.map((p) => (
            <div key={p.id} className="bg-[#121212] border border-neutral-800 rounded-lg p-5 card-lift">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-display font-black text-xl uppercase tracking-tight">{p.title}</div>
                  <div className="text-xs text-neutral-500 flex items-center gap-2 mt-2">
                    <CalendarBlank size={14} weight="bold" /> {p.practice_date} • {p.duration_minutes} min
                  </div>
                </div>
                <button onClick={() => remove(p.id)} className="text-neutral-500 hover:text-[#D90429]"><Trash size={16} weight="bold" /></button>
              </div>
              {p.activities?.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {p.activities.map((a, i) => <li key={i} className="text-sm text-neutral-300">• {a}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6">
        <div className="overline mb-3">Post-Practice Evaluation</div>
        <div className="text-sm text-neutral-400 mb-4">Quickly rate players after practice — one tap per skill.</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {players.map((p) => (
            <button
              key={p.id}
              onClick={() => { setEvalPlayer(p); setRatings({}); }}
              data-testid={`eval-open-${p.id}`}
              className="flex items-center gap-3 p-3 bg-[#0a0a0a] border border-neutral-800 rounded-md hover:border-[#D90429] text-left"
            >
              <PlayerAvatar player={p} size={36} />
              <div className="min-w-0">
                <div className="text-sm font-bold uppercase tracking-tight truncate">{p.first_name} {p.last_name}</div>
                <div className="overline">#{p.jersey_number || "—"}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {evalPlayer && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEvalPlayer(null)}>
          <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6 max-w-lg w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <PlayerAvatar player={evalPlayer} size={48} />
              <div>
                <div className="font-display font-black text-xl uppercase">{evalPlayer.first_name} {evalPlayer.last_name}</div>
                <div className="overline">Practice Evaluation</div>
              </div>
            </div>
            <div className="space-y-3">
              {PRACTICE_EVAL_FIELDS.map((f) => (
                <div key={f} className="flex items-center justify-between">
                  <div className="text-sm font-semibold capitalize">{f}</div>
                  <Stars value={ratings[f] || 0} onChange={(v) => setRatings((r) => ({ ...r, [f]: v }))} testid={`peval-${f}`} />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEvalPlayer(null)} className="flex-1 h-12 rounded-md border border-neutral-800 hover:bg-neutral-900 font-bold text-sm uppercase tracking-wider">Cancel</button>
              <button onClick={saveEval} data-testid="save-peval" className="flex-1 h-12 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-bold text-sm uppercase tracking-wider">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
